<i18n>
{
  "fr": {
    "logout-btn": "Déconnexion",
    "change-lang-btn": "English",
    "dashboard-label": "Tableau de bord",
    "general-compliance-label": "Conformité générale",
    "project-compliance-label": "Conformité par projet",
    "pending-tasks-label": "Tâches à faire",
    "manual-generation-label": "Génération du manuel",
    "questionnaire-esg-label": "Questionnaire ESG",
    "privacy-policy-btn" : "Politique de confidentialité",
    "privacy-policy-url" : "https://coreplatform.ca/core-politique-de-protection-des-renseignements-personnels-et-politique-de-confidentialite"
  },
  "en": {
    "logout-btn": "Log out",
    "change-lang-btn": "Français",
    "dashboard-label": "Dashboard",
    "general-compliance-label": "General compliance",
    "project-compliance-label": "Project compliance",
    "pending-tasks-label": "Tasks to do",
    "manual-generation-label": "Generate manual",
    "questionnaire-esg-label": "ESG Questionnaire",
    "privacy-policy-btn" : "Confidentiality Policy",
    "privacy-policy-url" : "https://coreplatform.ca/core-politique-de-protection-des-renseignements-personnels-et-politique-de-confidentialite"
  }
}
</i18n>


<template>
  <b-menu v-if="!isLoading" :activable="false">
    <b-menu-list>
      <!-- Nom de l'utilisateur -->
      <b-menu-item
        class="has-text-weight-semibold"
        icon="user"
        tag="router-link"
        :to="{ name: $consts.urls.URL_ACCOUNT_USER_PROFILE }"
        :label="name"
        :active="isCurrentPage('URL_ACCOUNT_USER_PROFILE')"
      ></b-menu-item>
      <!-- Nom de l'entreprise en cours -->
      <b-menu-item
        class="has-text-weight-semibold"
        icon="cog"
        tag="router-link"
        :to="{ name: $consts.urls.URL_BUSINESS_DETAIL }"
        :active="isCurrentPage('URL_BUSINESS_DETAIL')"
        :disabled="!isAdmin"
        :label="businessName"
      ></b-menu-item>
    </b-menu-list>

    <hr class="mx-12px my-16px has-text-grey-light" />

    <b-menu-list>
      <!-- Liens des pages -->
      <b-menu-item
        class="has-text-weight-semibold"
        tag="router-link"
        v-for="menuItem in primaryItems"
        :key="menuItem.id"
        :label="menuItem.label"
        :to="menuItem.to"
        :active="menuItem.isCurrentPage"
      ></b-menu-item>

      <!-- Liens des pages prismic -->
      <prismic-content :doctype="DOCUMENT_TYPE_MAIN_MENU" v-slot="{ content }">
        <template v-for="menuItem in content.items">
          <b-menu-item
            v-if="menuItem.page.uid"
            class="has-text-weight-semibold"
            tag="router-link"
            :key="menuItem.id"
            :label="$prismicText(menuItem.label)"
            :to="{ name: $consts.urls.URL_STATIC_PAGES, params: { uid: menuItem.page.uid } }"
            :active="$route.name === $consts.urls.URL_STATIC_PAGES && $route.params.uid === menuItem.page.uid"
          ></b-menu-item>
        </template>
      </prismic-content>
    </b-menu-list>

    <template v-if="secondaryItems.length > 0">
      <hr class="mx-12px my-16px has-text-grey-light" />

      <b-menu-list>
        <b-menu-item
          class="has-text-weight-semibold"
          tag="router-link"
          v-for="menuItem in secondaryItems"
          :key="menuItem.id"
          :label="menuItem.label"
          :to="menuItem.to"
          :active="menuItem.isCurrentPage"
        ></b-menu-item>
      </b-menu-list>
    </template>

    <hr class="mx-12px my-16px has-text-grey-light" />

    <b-menu-list>
      <!-- Lien de la politique de confidentialité -->
      <b-menu-item :label="$t('privacy-policy-btn')" tag="a" :href="$t('privacy-policy-url')" target="_blank"></b-menu-item>
      <!-- Lien de changement de langue -->
      <b-menu-item :label="$t('change-lang-btn')" @click="changeLang"></b-menu-item>
      <!-- Lien de déconnexion -->
      <b-menu-item :label="$t('logout-btn')" @click="logout"></b-menu-item>
    </b-menu-list>
  </b-menu>
</template>

<script>
import AuthentificationService from '@/services/authentification'
import BusinessService from '@/services/business'
import {
  BUSINESS_USER_TYPE_BUSINESS_ADMIN,
  BUSINESS_USER_TYPE_PROJECT_EDITOR,
  BUSINESS_USER_TYPE_BUSINESS_EDITOR,
  BUSINESS_USER_TYPE_GLOBAL_EDITOR
} from '@/consts/enums'
import { DOCUMENT_TYPE_MAIN_MENU } from '@/consts/prismic'
import i18n from '@/helpers/i18n'
import PrismicContent from '@/components/prismic/prismic-content'

export default {
  components: { PrismicContent },
  data() {
    return {
      me: null,
      business: null,
      businessRole: null,
      currentBusinessId: '',
      isLoading: true,
      DOCUMENT_TYPE_MAIN_MENU,
      currentUserId: null
    }
  },
  computed: {
    name: function () {
      return this.me ? `${this.me.profile.firstName} ${this.me.profile.lastName}` : ''
    },
    businessName: function () {
      return this.business ? this.business.name : ''
    },
    isAdmin() {
      return [BUSINESS_USER_TYPE_BUSINESS_ADMIN].includes(this.businessRole)
    },
    hasAccessToGeneralCompliance() {
      return [BUSINESS_USER_TYPE_BUSINESS_ADMIN, BUSINESS_USER_TYPE_GLOBAL_EDITOR, BUSINESS_USER_TYPE_BUSINESS_EDITOR].includes(
        this.businessRole
      )
    },
    hasAccessToProjectCompliance() {
      return [BUSINESS_USER_TYPE_BUSINESS_ADMIN, BUSINESS_USER_TYPE_GLOBAL_EDITOR, BUSINESS_USER_TYPE_PROJECT_EDITOR].includes(
        this.businessRole
      )
    },
    hasAccessToManualGeneration() {
      return [BUSINESS_USER_TYPE_BUSINESS_ADMIN, BUSINESS_USER_TYPE_GLOBAL_EDITOR].includes(this.businessRole)
    },
    primaryItems() {
      return this.createMenu((add) => {
        add('dashboard-label', 'URL_USER_DASHBOARD')

        if (this.hasAccessToGeneralCompliance) {
          add('general-compliance-label', 'URL_GENERAL_COMPLIANCE')
        }

        if (this.hasAccessToProjectCompliance) {
          add('project-compliance-label', 'URL_PROJECT_LIST')
        }

        add('pending-tasks-label', 'URL_PENDING_TASKS')

        if (this.hasAccessToManualGeneration) {
          add('manual-generation-label', 'URL_MANUAL_GENERATION')
        }
      })
    },
    secondaryItems() {
      return this.createMenu((add) => {
        if (this.isAdmin) {
          add('questionnaire-esg-label', 'URL_QUESTIONNAIRE_ESG')
        }
      })
    }
  },
  methods: {
    changeLang() {
      i18n.changeLang()
      this.setUserLanguage()
    },
    async setUserLanguage() {
      if (!this.currentUserId) return

      await this.$apollo.mutate({
        mutation: this.$options.mutation.UpdateUserLanguage,
        variables: {
          userId: this.currentUserId,
          language: { value: i18n.userLanguage() }
        }
      })

      this.$success()
    },
    logout: AuthentificationService.logout,
    isCurrentPage: function (pageKey) {
      return this.$route.path.startsWith(this.$router.resolve({ name: pageKey }).href)
    },
    isCurrentStaticPage: function (uid) {
      return this.$route.name === this.$consts.urls.URL_STATIC_PAGES && this.$route.params.uid === uid
    },
    createMenu(buildMenu) {
      const items = []

      const add = (label, toKey) => {
        items.push({
          label: this.$t(label),
          to: { name: this.$consts.urls[toKey] },
          isCurrentPage: this.isCurrentPage(toKey)
        })
      }

      buildMenu(add)

      return items
    }
  },
  async mounted() {
    const [business, userType] = await Promise.all([BusinessService.getCurrentBusiness(), AuthentificationService.getUserType()])

    this.currentBusinessId = business.id
    this.userType = userType

    this.isLoading = false
  },
  apollo: {
    infos: {
      query() {
        return this.$options.query.NavUser_Infos
      },
      variables() {
        return {
          businessId: this.currentBusinessId
        }
      },
      skip() {
        return !this.currentBusinessId
      },
      update(data) {
        if (!data) return
        this.me = data.me

        if (data.me && data.me.userBusiness && data.me.userBusiness.length > 0) {
          this.businessRole = data.me.userBusiness[0].userType
          this.business = data.me.userBusiness[0].business
          this.currentUserId = data.me.id
        }
      }
    }
  }
}
</script>

<graphql>
query NavUser_Infos($businessId: ID!) {
  me {
    id
    profile {
      id
      firstName
      lastName
      language
    }
    userBusiness(businessId: { value: $businessId }) {
      id
      position
      userType
      business {
        id
        name
      }
    }
  }
}
mutation UpdateUserLanguage($userId: ID!, $language: UserLanguageMaybe) {
  updateUserProfile(input: { userId: $userId, language: $language }) {
    user {
      id
      profile {
        id
        language
      }
    }
  }
}
</graphql>
